/* ============================================================================
 * Iconographie Ki Para — LA SEULE source d'icônes du frontend.
 * (Ki_Para_Iconographie.html, owner 2026-09-01 — brand kit « Clinique »)
 *
 * Règles du kit :
 *  1. Bibliothèque unique : Lucide (lucide.dev), trait 1.5 — jamais d'icônes
 *     pleines ni d'une autre bibliothèque (react-icons est banni).
 *  2. Tailles : 20 px interface (boutons, badges, champs) · 24 px navigation
 *     · 14–16 px petits badges. Ici les icônes font 1em et suivent donc le
 *     font-size du parent (comme l'ancienne bibliothèque) ; on peut forcer
 *     avec la prop `size={20}`.
 *  3. Couleur par défaut héritée (currentColor) : Spruce sur fond clair,
 *     #F4F4F6 sur fond sombre. États : corail (alerte/impayé/rupture),
 *     or (vigilance), gris (neutre) — portés par la classe du parent.
 *  4. Une icône ne va jamais seule : toujours un libellé (sauf bouton-icône
 *     avec title/aria-label).
 *
 * Toute nouvelle icône : l'ajouter ICI (import lucide + export ki(...)),
 * jamais d'import direct de lucide-react dans un composant.
 * ========================================================================== */
import React from 'react';
import {
  AlertCircle, AlertTriangle, ArrowDown, ArrowDownWideNarrow, ArrowLeft,
  ArrowRight, ArrowUp, Award, Ban, Banknote, BarChart3, Bookmark, Boxes,
  Building2, Calculator, Calendar, CalendarCheck, CalendarDays, CalendarRange,
  Camera, Check, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, KeyRound,
  ChevronUp, CircleDollarSign, CircleUser, ClipboardList, Clock, Coins, ConciergeBell, Contact,
  Cpu, CreditCard, ExternalLink, Eye, EyeOff, Facebook, FileText, Flame,
  FlaskConical, Flower2, FolderOpen, Gem, Gift, Globe, HandCoins, HelpCircle,
  History, Home, Hourglass, Image, Info, Instagram, Landmark, Layers,
  LayoutGrid, Link2, LineChart, List, Loader2, Lock, LogOut, Mail, MapPin,
  MessageCircle, Minus, Moon, MoreHorizontal, MoreVertical, Network,
  Package, PackageCheck, PackageOpen, PackageX, Pencil, Percent, Phone,
  PieChart, Pin, Plus, Printer,
  Receipt, ReceiptText, RefreshCw, RotateCcw, Save, ScanBarcode, Search, Send, Settings,
  ShieldCheck, ShoppingBag, ShoppingCart, SlidersHorizontal, Sparkles, Star,
  Sun, Tag, Tags, ToggleLeft, ToggleRight, Trash2, TrendingDown, TrendingUp,
  Trophy, Truck, Undo2, Unlock, User, UserCheck, UserCog, UserPlus, Users,
  Wallet, Warehouse, Wifi, X, XCircle,
} from 'lucide-react';

/* Défauts du kit : trait 1.5, taille 1em (suit le font-size du parent,
 * comme react-icons avant — les classes text-* existantes gardent la main). */
const ki = (Icon) => {
  const C = (props) => <Icon size="1em" strokeWidth={1.5} {...props} />;
  C.displayName = `Ki${Icon.displayName || Icon.name || 'Icon'}`;
  return C;
};

/* ---- Navigation ---- */
export const IcHome = ki(Home);                 /* Tableau de bord */
export const IcShoppingCart = ki(ShoppingCart); /* Ventes */
export const IcPackage = ki(Package);           /* Produits */
export const IcUsers = ki(Users);               /* Clients */
export const IcShoppingBag = ki(ShoppingBag);   /* Achat */
export const IcSettings = ki(Settings);         /* Réglages */
export const IcBanknote = ki(Banknote);         /* Paiement / montants */
export const IcBarChart = ki(BarChart3);        /* Statistiques */
export const IcUserCog = ki(UserCog);           /* Utilisateurs */
export const IcLogOut = ki(LogOut);             /* Déconnexion */

/* ---- Modules & métier ---- */
export const IcScanBarcode = ki(ScanBarcode);   /* Scanner */
export const IcReceiptText = ki(ReceiptText);   /* Reçu (facture fournisseur) */
export const IcReceipt = ki(Receipt);           /* Reçu (ticket) */
export const IcClipboardList = ki(ClipboardList); /* Réservation */
export const IcHistory = ki(History);           /* Historique */
export const IcTruck = ki(Truck);               /* Fournisseurs */
export const IcFlask = ki(FlaskConical);        /* Laboratoires */
export const IcFileText = ki(FileText);         /* Factures */
export const IcSparkles = ki(Sparkles);         /* Soins */
export const IcFlower = ki(Flower2);            /* Soin (onglet résa) */
export const IcGift = ki(Gift);                 /* Points */
export const IcCoins = ki(Coins);               /* Encaisser */
export const IcTrophy = ki(Trophy);             /* Top clients */
export const IcCalculator = ki(Calculator);     /* Nouvelle vente (caisse) */
export const IcPercent = ki(Percent);           /* Promo */
export const IcTags = ki(Tags);                 /* Catégories */
export const IcTag = ki(Tag);
export const IcStar = ki(Star);                 /* Fidélité / score */
export const IcCreditCard = ki(CreditCard);     /* Carte fidélité */
export const IcConciergeBell = ki(ConciergeBell); /* Services */
export const IcGem = ki(Gem);
export const IcAward = ki(Award);
export const IcLayers = ki(Layers);
export const IcBoxes = ki(Boxes);
export const IcPackageOpen = ki(PackageOpen);
export const IcWarehouse = ki(Warehouse);
export const IcBuilding = ki(Building2);
export const IcLandmark = ki(Landmark);
export const IcCpu = ki(Cpu);
export const IcNetwork = ki(Network);

/* ---- Actions d'interface ---- */
export const IcSearch = ki(Search);
export const IcFilters = ki(SlidersHorizontal); /* Filtres */
export const IcPlus = ki(Plus);                 /* Ajouter */
export const IcMinus = ki(Minus);               /* Quantité − */
export const IcPencil = ki(Pencil);             /* Modifier */
export const IcTrash = ki(Trash2);              /* Supprimer (corail) */
export const IcPrinter = ki(Printer);           /* Imprimer */
export const IcRefresh = ki(RefreshCw);         /* Actualiser */
export const IcClose = ki(X);                   /* Fermer */
export const IcCheck = ki(Check);               /* Valider */
export const IcChevronDown = ki(ChevronDown);
export const IcChevronUp = ki(ChevronUp);
export const IcChevronLeft = ki(ChevronLeft);
export const IcChevronRight = ki(ChevronRight);
export const IcCalendar = ki(Calendar);
export const IcCalendarCheck = ki(CalendarCheck);
export const IcCalendarDays = ki(CalendarDays);
export const IcCalendarRange = ki(CalendarRange);
export const IcList = ki(List);                 /* Vue liste */
export const IcLayoutGrid = ki(LayoutGrid);     /* Actions rapides */
export const IcEye = ki(Eye);                   /* Voir */
export const IcEyeOff = ki(EyeOff);
export const IcArrowLeft = ki(ArrowLeft);       /* Retour */
export const IcArrowRight = ki(ArrowRight);
export const IcArrowUp = ki(ArrowUp);
export const IcArrowDown = ki(ArrowDown);
export const IcSortDesc = ki(ArrowDownWideNarrow);
export const IcSave = ki(Save);
export const IcSend = ki(Send);
export const IcLink = ki(Link2);
export const IcExternalLink = ki(ExternalLink);
export const IcUndo = ki(Undo2);
export const IcMoreHorizontal = ki(MoreHorizontal);
export const IcMoreVertical = ki(MoreVertical);
export const IcPin = ki(Pin);
export const IcLoader = ki(Loader2);            /* + className animate-spin */
export const IcCamera = ki(Camera);
export const IcImage = ki(Image);
export const IcFolderOpen = ki(FolderOpen);
export const IcToggleOn = ki(ToggleRight);
export const IcToggleOff = ki(ToggleLeft);
export const IcHelp = ki(HelpCircle);
export const IcInfo = ki(Info);

/* ---- États & alertes (la couleur vient du parent) ---- */
export const IcCheckCircle = ki(CheckCircle2);  /* Payé / Reçu */
export const IcAlertCircle = ki(AlertCircle);   /* Impayé (corail) */
export const IcAlertTriangle = ki(AlertTriangle); /* Stock bas (or) */
export const IcXCircle = ki(XCircle);
export const IcLock = ki(Lock);                 /* Bloquée */
export const IcUnlock = ki(Unlock);
/* identification du vendeur à la caisse */
export const IcKeyRound = ki(KeyRound);         /* code de caisse */
export const IcClock = ki(Clock);               /* En attente (gris) */
export const IcFlame = ki(Flame);               /* N vendus (30j, or) */
export const IcPackageX = ki(PackageX);         /* Rupture (corail) */
export const IcPackageCheck = ki(PackageCheck); /* En stock */
export const IcTrendingUp = ki(TrendingUp);     /* Tendance ▲ */
export const IcTrendingDown = ki(TrendingDown); /* Tendance ▼ (corail) */
export const IcBookmark = ki(Bookmark);         /* Réservé ×N */
export const IcWallet = ki(Wallet);             /* Crédit dû (corail) */
export const IcHandCoins = ki(HandCoins);       /* Crédit payé */
export const IcCircleDollarSign = ki(CircleDollarSign); /* Partiel · reste N DT (Etiquettes-2 §2) */
export const IcRotateCcw = ki(RotateCcw);       /* badge « Crédit payé » (Etiquettes-2 §2) */
export const IcMessageCircle = ki(MessageCircle); /* Notes-chat */
export const IcHourglass = ki(Hourglass);
export const IcBan = ki(Ban);
export const IcLineChart = ki(LineChart);
export const IcPieChart = ki(PieChart);
export const IcSun = ki(Sun);
export const IcMoon = ki(Moon);
export const IcWifi = ki(Wifi);
export const IcGlobe = ki(Globe);
export const IcMapPin = ki(MapPin);

/* ---- Personnes & contact ---- */
export const IcUser = ki(User);
export const IcUserPlus = ki(UserPlus);
export const IcUserCheck = ki(UserCheck);       /* catégorie client */
export const IcUserCircle = ki(CircleUser);
export const IcShieldCheck = ki(ShieldCheck);   /* admin */
export const IcContact = ki(Contact);           /* carte d'identité */
export const IcPhone = ki(Phone);
export const IcMail = ki(Mail);
export const IcFacebook = ki(Facebook);
export const IcInstagram = ki(Instagram);
/* Lucide n'a pas d'icône WhatsApp (marques limitées) : on prend la bulle
 * message — à revoir si Ahmed veut la vraie marque. */
export const IcWhatsapp = ki(MessageCircle);

/* ============================================================================
 * TABLE DE MIGRATION react-icons/fa → theme/icons (2026-09-01)
 * FaTimes→IcClose · FaPlus→IcPlus · FaMoneyBillWave→IcBanknote ·
 * FaSearch→IcSearch · FaBox→IcPackage · FaChevronRight→IcChevronRight ·
 * FaGift→IcGift · FaEdit→IcPencil · FaServicestack→IcSparkles ·
 * FaBoxOpen→IcPackageOpen · FaFileInvoiceDollar→IcReceiptText ·
 * FaTrashAlt→IcTrash · FaTrash→IcTrash · FaChevronLeft→IcChevronLeft ·
 * FaClipboardList→IcClipboardList · FaCheckCircle→IcCheckCircle ·
 * FaSave→IcSave · FaFilter→IcFilters · FaExclamationTriangle→IcAlertTriangle ·
 * FaSpa→IcFlower · FaCheck→IcCheck · FaCalendarAlt→IcCalendar ·
 * FaArrowLeft→IcArrowLeft · FaUser→IcUser · FaTruck→IcTruck ·
 * FaImage→IcImage · FaTags→IcTags · FaExclamationCircle→IcAlertCircle ·
 * FaTrophy→IcTrophy · FaChartLine→IcLineChart · FaUsers→IcUsers ·
 * FaShoppingBag→IcShoppingBag · FaUserAlt→IcUser ·
 * FaShoppingCart→IcShoppingCart · FaAward→IcAward · FaMinus→IcMinus ·
 * FaHistory→IcHistory · FaFileInvoice→IcFileText ·
 * FaConciergeBell→IcConciergeBell · FaClock→IcClock · FaWallet→IcWallet ·
 * FaTag→IcTag · FaReceipt→IcReceipt · FaPhoneAlt→IcPhone ·
 * FaLayerGroup→IcLayers · FaChartBar→IcBarChart · FaStar→IcStar ·
 * FaEnvelope→IcMail · FaCreditCard→IcCreditCard ·
 * FaChevronDown→IcChevronDown · FaUserFriends→IcUsers · FaPhone→IcPhone ·
 * FaFlask→IcFlask · FaPercent→IcPercent · FaPercentage→IcPercent ·
 * FaLock→IcLock · FaLockOpen→IcToggleOn?non→voir note* · FaCoins→IcCoins ·
 * FaCamera→IcCamera · FaBuilding→IcBuilding · FaSun→IcSun · FaMoon→IcMoon ·
 * FaMapMarkerAlt→IcMapPin · FaExternalLinkAlt→IcExternalLink ·
 * FaSignOutAlt→IcLogOut · FaHandHoldingUsd→IcHandCoins ·
 * FaComments→IcMessageCircle · FaCashRegister→IcCalculator ·
 * FaUserPlus→IcUserPlus · FaPrint→IcPrinter · FaMicrochip→IcCpu ·
 * FaGem→IcGem · FaCog→IcSettings · FaChevronUp→IcChevronUp ·
 * FaWarehouse→IcWarehouse · FaUserTag→IcUserCheck ·
 * FaUniversity→IcLandmark · FaRegCalendarAlt→IcCalendar ·
 * FaPaperPlane→IcSend · FaLink→IcLink · FaIdCard→IcContact ·
 * FaHourglassHalf→IcHourglass · FaEllipsisH→IcMoreHorizontal ·
 * FaEllipsisV→IcMoreVertical · FaCalendarCheck→IcCalendarCheck ·
 * FaUserShield→IcShieldCheck · FaToggleOn→IcToggleOn ·
 * FaToggleOff→IcToggleOff · FaThumbtack→IcPin · FaSpinner→IcLoader ·
 * FaBan→IcBan · FaUserCircle→IcUserCircle · FaUndo→IcUndo ·
 * FaTimesCircle→IcXCircle · FaSitemap→IcNetwork ·
 * FaRegQuestionCircle→IcHelp · FaRegCreditCard→IcCreditCard ·
 * FaPencilAlt→IcPencil · FaPen→IcPencil · FaMoneyCheckAlt→IcBanknote ·
 * FaListUl→IcList · FaHome→IcHome · FaCubes→IcBoxes · FaBoxes→IcBoxes ·
 * FaCalendarWeek→IcCalendarRange · FaCalendarDay→IcCalendarDays ·
 * FaArrowUp→IcArrowUp · FaArrowDown→IcArrowDown · FaArrowRight→IcArrowRight ·
 * FaFire→IcFlame · FaEye→IcEye · FaEyeSlash→IcEyeOff ·
 * FaBookmark→IcBookmark · FaWifi→IcWifi · FaWhatsapp→IcWhatsapp ·
 * FaUserCog→IcUserCog · FaThLarge→IcLayoutGrid · FaSync→IcRefresh ·
 * FaSortAmountDown→IcSortDesc · FaInstagram→IcInstagram ·
 * FaInfoCircle→IcInfo · FaGlobe→IcGlobe · FaFileAlt→IcFileText ·
 * FaFacebookF→IcFacebook · FaChartPie→IcPieChart ·
 * FaCartPlus→IcShoppingCart · FaBarcode→IcScanBarcode ·
 * FaFolderOpen→IcFolderOpen
 * (*FaLockOpen→IcUnlock, export ci-dessous.)
 * ========================================================================== */
export { ki };
